import React from "react";
import {
  FiX,
  FiUser,
  FiCalendar,
  FiPackage,
  FiTruck,
  FiAlertCircle,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";

const ConsignmentApplicationDetails = ({
  application,
  open,
  onClose,
  statusLabels,
  statusColors,
}) => {
  const { t } = useTranslation();

  if (!open || !application) return null;

  const mediaUrl = (url) => process.env.REACT_APP_IMAGE_URL + url;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-4xl transform transition-all">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 to-purple-700 rounded-t-3xl p-6 text-white">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2 text-left rtl:text-right">
                  {t("dashboard.consignments.details.title", {
                    id: application.id,
                  })}
                </h2>
                <div className="flex items-center justify-end gap-3">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      statusColors?.[application.status] ||
                      "bg-gray-200 text-gray-800"
                    }`}
                  >
                    {statusLabels?.[application.status] || application.status}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 p-2 hover:bg-white hover:bg-opacity-20 rounded-xl transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-[70vh] overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Basic info */}
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-left rtl:text-right">
                    {t("dashboard.consignments.details.basicInfoTitle")}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-300">
                        {t("dashboard.consignments.common.fields.category")}
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {application.category?.name ||
                          t("dashboard.consignments.details.notAvailable")}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-300">
                        {t("dashboard.consignments.common.fields.client")}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {application.user?.name ||
                            t("dashboard.consignments.details.notAvailable")}{" "}
                          ({application.user?.number || "-"})
                        </span>
                        <FiUser className="w-4 h-4 text-blue-500" />
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-300">
                        {t("dashboard.consignments.common.fields.expert")}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {application.expert?.name ||
                            t("dashboard.consignments.details.notAvailable")}{" "}
                          ({application.expert?.number || "-"})
                        </span>
                        <FiUser className="w-4 h-4 text-purple-500" />
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-300">
                        {t("dashboard.consignments.common.fields.createdAt")}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {new Date(application.createdAt).toLocaleString(
                            "ar-EG"
                          )}
                        </span>
                        <FiCalendar className="w-4 h-4 text-green-500" />
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-300">
                        {t("dashboard.consignments.details.updatedAt")}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {new Date(application.updatedAt).toLocaleString(
                            "ar-EG"
                          )}
                        </span>
                        <FiCalendar className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-300">
                        {t(
                          "dashboard.consignments.details.clientConfirmation"
                        )}
                      </span>
                      <span
                        className={
                          application.userConfirmed
                            ? "text-green-600 font-semibold"
                            : "text-gray-400"
                        }
                      >
                        {application.userConfirmed
                          ? t("dashboard.consignments.details.confirmed")
                          : t("dashboard.consignments.details.notConfirmed")}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-300">
                        {t(
                          "dashboard.consignments.details.expertConfirmation"
                        )}
                      </span>
                      <span
                        className={
                          application.expertConfirmed
                            ? "text-green-600 font-semibold"
                            : "text-gray-400"
                        }
                      >
                        {application.expertConfirmed
                          ? t("dashboard.consignments.details.confirmed")
                          : t("dashboard.consignments.details.notConfirmed")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Shipping info */}
                {(application.AWBNo || application.refNo) && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-left rtl:text-right flex items-center justify-end gap-2">
                      <FiTruck className="w-5 h-5" />
                      {t("dashboard.consignments.details.shippingInfoTitle")}
                    </h3>
                    <div className="space-y-3">
                      {application.AWBNo && (
                        <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                          <span className="text-gray-600 dark:text-gray-300">
                            {t("dashboard.consignments.details.awbNumber")}
                          </span>
                          <span className="font-mono text-gray-900 dark:text-white">
                            {application.AWBNo}
                          </span>
                        </div>
                      )}
                      {application.refNo && (
                        <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                          <span className="text-gray-600 dark:text-gray-300">
                            {t("dashboard.consignments.details.refNumber")}
                          </span>
                          <span className="font-mono text-gray-900 dark:text-white">
                            {application.refNo}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Rejection reason */}
                {application.rejectionReason && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-red-700 dark:text-red-300 mb-2 text-left rtl:text-right flex items-center justify-end gap-2">
                      <FiAlertCircle className="w-5 h-5" />
                      {t("dashboard.consignments.details.rejectionReasonTitle")}
                    </h3>
                    <p className="text-red-600 dark:text-red-400 text-left rtl:text-right">
                      {application.rejectionReason}
                    </p>
                  </div>
                )}

                {application.auction_id && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-6 text-left rtl:text-right">
                    <span className="text-green-700 dark:text-green-300 font-semibold">
                      {t("dashboard.consignments.details.convertedToAuction", {
                        id: application.auction_id,
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* Products */}
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-left rtl:text-right flex items-center justify-end gap-2">
                    <FiPackage className="w-5 h-5" />
                    {t("dashboard.consignments.details.productsTitle", {
                      count: application.products?.length || 0,
                    })}
                  </h3>

                  {application.products?.length > 0 ? (
                    <div className="space-y-4">
                      {application.products.map((product) => (
                        <div
                          key={product.id}
                          className="bg-white dark:bg-gray-700 rounded-xl p-4 border border-gray-200 dark:border-gray-600"
                        >
                          <div className="text-left rtl:text-right font-semibold text-gray-900 dark:text-white mb-3">
                            {product.name}
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                            <div className="text-left rtl:text-right">
                              <span className="text-gray-500 dark:text-gray-400 block">
                                {t("dashboard.consignments.details.weight")}
                              </span>
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {t(
                                  "dashboard.consignments.details.weightValue",
                                  { weight: product.weight }
                                )}
                              </span>
                            </div>
                            <div className="text-left rtl:text-right">
                              <span className="text-gray-500 dark:text-gray-400 block">
                                {t("dashboard.consignments.details.minPrice")}
                              </span>
                              <span className="font-semibold text-blue-600 dark:text-blue-400">
                                {t(
                                  "dashboard.consignments.details.priceValue",
                                  { price: product.minPrice }
                                )}
                              </span>
                            </div>
                            <div className="text-left rtl:text-right">
                              <span className="text-gray-500 dark:text-gray-400 block">
                                {t("dashboard.consignments.details.maxPrice")}
                              </span>
                              <span className="font-semibold text-green-600 dark:text-green-400">
                                {t(
                                  "dashboard.consignments.details.priceValue",
                                  { price: product.maxPrice }
                                )}
                              </span>
                            </div>
                          </div>

                          {product.media?.length > 0 && (
                            <div className="grid grid-cols-4 gap-2">
                              {product.media.map((media) => (
                                <div
                                  key={media.id}
                                  className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-600"
                                >
                                  {media.type === "Video" ? (
                                    <video
                                      src={mediaUrl(media.url)}
                                      className="w-full h-full object-cover"
                                      controls
                                    />
                                  ) : (
                                    <img
                                      src={mediaUrl(media.url)}
                                      alt={product.name}
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-white dark:bg-gray-700 rounded-xl">
                      <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 dark:text-gray-400">
                        {t("dashboard.consignments.details.noProducts")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800 rounded-b-3xl flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t("dashboard.consignments.details.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsignmentApplicationDetails;
